import { computed } from 'vue'
import { type ComponentNode, ComponentTypes } from '@canvas-builder/types/canvas'
import { useComponentStyle } from './useComponentStyle'
import useCanvas from './useCanvas'

// Default positioning constants for scribble children
export const SCRIBBLE_CHILD_DEFAULTS = {
  POSITION: {
    TOP: '76px',
    LEFT: '16px',
  },
  DIMENSIONS: {
    DEFAULT_WIDTH: '800px',
    DEFAULT_HEIGHT: '300px',
    EMBEDDABLE_WIDTH: '400px',
    EMBEDDABLE_HEIGHT: '300px',
  },
} as const

export interface ScribbleChildConfig {
  id: string
  parentType?: ComponentTypes
  style?: Record<string, any>
  data?: any
  defaultWidth?: string
  defaultHeight?: string
  /** Whether to update component data with dimensions (needed for Embeddable) */
  updateDataWithDimensions?: boolean
  /** Custom style updates beyond the defaults */
  customStyleUpdates?: Record<string, any>
  /** Whether to exclude height from style updates (for components like Text that should auto-size) */
  excludeHeight?: boolean
}

/**
 * Composable for handling scribble child component initialization and common functionality
 * Consolidates the handleAddedToScribble pattern used across multiple components
 */
export function useScribbleChild(config: ScribbleChildConfig) {
  const {
    id,
    parentType,
    style,
    data,
    defaultWidth,
    defaultHeight,
    updateDataWithDimensions,
    customStyleUpdates,
    excludeHeight,
  } = config

  const { componentNodesById, updateNodeDataById } = useCanvas()

  const componentNode = computed(() => componentNodesById.value[id] as ComponentNode | null)
  const { createStyleUpdate } = useComponentStyle(componentNode)

  const isScribbleChild = computed(() => {
    return parentType === ComponentTypes.Scribble
  })

  /**
   * Handle component initialization when added to scribble
   * This consolidates the common pattern found across multiple components
   */
  function handleAddedToScribble(emit: (event: 'style', style: Record<string, any>, id?: string) => void) {
    if (!isScribbleChild.value) return

    const width = defaultWidth || style?.width || SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.DEFAULT_WIDTH
    const height = defaultHeight || style?.height || SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.DEFAULT_HEIGHT

    // Update component data with dimensions if requested (e.g., for Embeddable)
    if (updateDataWithDimensions && data && updateNodeDataById) {
      updateNodeDataById(id, {
        ...data,
        width,
        height,
      })
    }

    // Create style update with positioning and dimensions
    const styleUpdate: Record<string, any> = {
      position: 'absolute' as const,
      top: SCRIBBLE_CHILD_DEFAULTS.POSITION.TOP,
      left: SCRIBBLE_CHILD_DEFAULTS.POSITION.LEFT,
      width,
      ...customStyleUpdates,
    }

    // Only include height if not excluded (e.g., Text components should auto-size their height)
    if (!excludeHeight) {
      styleUpdate.height = height
    }

    emit('style', createStyleUpdate(styleUpdate))
  }

  return {
    isScribbleChild,
    handleAddedToScribble,
    componentNode,
    createStyleUpdate,
  }
}

/**
 * Utility function to get embeddable-specific configuration
 */
export function getEmbeddableScribbleConfig(id: string, parentType?: ComponentTypes, data?: any, style?: any) {
  return {
    id,
    parentType,
    style,
    data,
    defaultWidth: SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.EMBEDDABLE_WIDTH,
    defaultHeight: SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.EMBEDDABLE_HEIGHT,
    updateDataWithDimensions: true,
  }
}

/**
 * Utility function to get text-specific configuration
 */
export function getTextScribbleConfig(id: string, parentType?: ComponentTypes, style?: any) {
  return {
    id,
    parentType,
    style,
    defaultWidth: style?.width || SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.DEFAULT_WIDTH,
    excludeHeight: true,
  }
}

/**
 * Utility function to get multimedia-specific configuration with proportional dimensions
 */
export function getMultimediaScribbleConfig(
  id: string,
  parentType?: ComponentTypes,
  style?: any,
  calculateDimensions?: () => { width: number; height: number },
) {
  let defaultWidth: string = SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.DEFAULT_WIDTH
  let defaultHeight: string = SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.DEFAULT_HEIGHT

  if (calculateDimensions) {
    const dimensions = calculateDimensions()
    defaultWidth = `${dimensions.width}px`
    defaultHeight = dimensions.height ? `${dimensions.height}px` : 'auto'
  }

  return {
    id,
    parentType,
    style,
    defaultWidth: style?.width ? parseInt(style.width as string) + 'px' : defaultWidth,
    defaultHeight: style?.height ? parseInt(style.height as string) + 'px' : defaultHeight,
  }
}

/**
 * Utility function to get dynamic data table-specific configuration
 */
export function getDynamicDataTableScribbleConfig(id: string, parentType?: ComponentTypes, style?: any) {
  return {
    id,
    parentType,
    style,
    defaultWidth: style?.width || SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.DEFAULT_WIDTH,
    defaultHeight: style?.height || SCRIBBLE_CHILD_DEFAULTS.DIMENSIONS.DEFAULT_HEIGHT,
  }
}
